import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { PartyMascot } from "../../party/PartyMascot";
import { getPartyShellChrome } from "../../party/modePartyCards";
import { decodePartyCastPayload } from "../../party/partyCastPayload";
import type { PartyShellPalette } from "../../theme/theme";

function CastBrandLogo() {
  const { t } = useTranslation();
  return (
    <Box
      component="img"
      src="/assets/kan.png"
      alt={t("common.appName")}
      sx={{
        height: { xs: 44, sm: 52 },
        width: "auto",
        maxWidth: "min(55vw, 200px)",
        objectFit: "contain",
        display: "block",
        mx: "auto",
        mb: 0.25,
        filter: "drop-shadow(0 6px 20px rgba(0,0,0,0.45))",
      }}
    />
  );
}

function ImposterCastHints({ hints, hintsLabel }: { hints: unknown; hintsLabel: string }) {
  const lines = Array.isArray(hints) ? hints.filter((x): x is string => typeof x === "string") : [];
  if (lines.length === 0) return null;
  return (
    <Box sx={{ mt: 2, width: "100%", maxWidth: "min(100%, 40ch)", mx: "auto" }}>
      <Typography className="party-mono" sx={{ fontSize: 10, opacity: 0.55, mb: 0.75, textAlign: "center" }}>
        {hintsLabel}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 1.25,
          rowGap: 0.75,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {lines.map((x, i) => (
          <Typography key={`${i}:${x}`} component="span" sx={{ fontSize: 15, fontWeight: 700, color: "inherit" }}>
            · {x}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function RevealCard({
  children,
  paper,
}: {
  children: React.ReactNode;
  paper: boolean;
}) {
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  return (
    <Box
      className="party-kan-card party-anim-fade-scale-in"
      sx={
        {
          ["--card-bg" as string]: paper
            ? partyPal.paper
            : "linear-gradient(160deg, #2A0B1A, #1A0520)",
          color: paper ? partyPal.paperInk : partyPal.ink,
          py: 4,
          px: 3,
          textAlign: "center",
          maxWidth: 400,
          width: "100%",
          mx: "auto",
        } as React.CSSProperties
      }
    >
      {children}
    </Box>
  );
}

export default function PartyCastRevealPage() {
  const { shellId: shellIdParam } = useParams<{ shellId: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;

  const shellId = shellIdParam ?? "";
  const payload = useMemo(
    () => decodePartyCastPayload(searchParams.get("d")),
    [searchParams],
  );

  const chrome = getPartyShellChrome(shellId);
  const dir = payload?.rtl ? "rtl" : "ltr";

  if (!payload || payload.shell !== shellId) {
    return (
      <Box className="party-shell-root" dir={dir} lang={dir === "rtl" ? "ar" : "en"} sx={{ minHeight: "100dvh", p: 3, pt: 5 }}>
        <Stack spacing={2.5} alignItems="center" sx={{ maxWidth: 440, mx: "auto" }}>
          <CastBrandLogo />
          <Typography className="party-display" sx={{ color: partyPal.hot, textAlign: "center" }}>
            {t("party.cast.invalidLink")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  const cardBg = chrome.bg as string;

  return (
    <Box
      className="party-shell-root"
      dir={dir}
      lang={dir === "rtl" ? "ar" : "en"}
      data-party-theme="midnight"
      data-party-type="rubik"
      sx={{
        minHeight: "100dvh",
        p: 2.5,
        pt: 4,
        background: cardBg,
        backgroundAttachment: "fixed",
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 440, mx: "auto" }}>
        <CastBrandLogo />
        <PartyMascot shellId={shellId} size={88} />

        {payload.layout === "imposter_role" && (
          <RevealCard paper={false}>
            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", color: partyPal.hot, mt: 0.5 }}>
              {payload.h1}
            </Typography>
            <Typography className="party-display" sx={{ fontSize: 48, color: partyPal.hot, mt: 1 }}>
              {payload.h2}
            </Typography>
            <Typography sx={{ mt: 1.5, color: partyPal.inkDim, fontSize: 14, lineHeight: 1.5 }}>{payload.h3}</Typography>
          </RevealCard>
        )}

        {payload.layout === "imposter_word" && (
          <RevealCard paper>
            {payload.h2 ? (
              <>
                <Typography
                  className="party-mono"
                  sx={{ fontSize: 11, letterSpacing: "0.2em", color: `color-mix(in oklab, ${partyPal.paperInk} 55%, transparent)` }}
                >
                  {payload.h1}
                </Typography>
                <Typography className="party-display" sx={{ fontSize: 40, mt: 1.25, color: "inherit" }}>
                  {payload.h2}
                </Typography>
                {payload.h3 ? (
                  <Typography
                    sx={{
                      mt: 1.75,
                      fontSize: 14,
                      color: `color-mix(in oklab, ${partyPal.paperInk} 72%, transparent)`,
                      lineHeight: 1.5,
                      maxWidth: "30ch",
                      mx: "auto",
                    }}
                  >
                    {payload.h3}
                  </Typography>
                ) : null}
                <Typography
                  className="party-mono"
                  sx={{
                    fontSize: 11,
                    letterSpacing: "0.2em",
                    color: `color-mix(in oklab, ${partyPal.paperInk} 60%, transparent)`,
                    mt: 3,
                  }}
                >
                  {t("party.playImposter.secretWordLabel")}
                </Typography>
                <Typography className="party-display" sx={{ fontSize: 64, mt: 1.25, color: "inherit" }}>
                  {payload.w}
                </Typography>
                <ImposterCastHints hints={payload.hints} hintsLabel={t("party.playImposter.hintsHeading")} />
              </>
            ) : (
              <>
                {payload.h1 ? (
                  <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", opacity: 0.65, mt: 0.5 }}>
                    {payload.h1}
                  </Typography>
                ) : null}
                <Typography className="party-display" sx={{ fontSize: 64, mt: 1.25, color: "inherit" }}>
                  {payload.w}
                </Typography>
                <ImposterCastHints hints={payload.hints} hintsLabel={t("party.playImposter.hintsHeading")} />
              </>
            )}
          </RevealCard>
        )}

        {(payload.layout === "acting_word" || payload.layout === "who_word") && (
          <RevealCard paper>
            {payload.h1 ? (
              <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", opacity: 0.65, mt: 0.5 }}>
                {payload.h1}
              </Typography>
            ) : null}
            <Typography className="party-display" sx={{ fontSize: payload.layout === "who_word" ? 52 : 64, mt: 1.25, color: "inherit" }}>
              {payload.w}
            </Typography>
          </RevealCard>
        )}

        {payload.layout === "guess_word" && (
          <RevealCard paper>
            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", opacity: 0.65 }}>
              {payload.h1}
            </Typography>
            <Typography className="party-display" sx={{ fontSize: 56, mt: 1, color: "inherit" }}>
              {payload.w}
            </Typography>
            {payload.f && payload.f.length > 0 ? (
              <Box sx={{ mt: 2, textAlign: "start" }}>
                <Typography className="party-mono" sx={{ fontSize: 10, opacity: 0.55, mb: 0.75 }}>
                  {payload.h2}
                </Typography>
                <Stack spacing={0.75}>
                  {payload.f.map((x) => (
                    <Typography key={x} sx={{ fontSize: 15, fontWeight: 700, color: "inherit" }}>
                      · {x}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </RevealCard>
        )}

        {payload.layout === "quiz" && (
          <RevealCard paper={false}>
            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.15em", color: partyPal.inkDim }}>
              {payload.h1}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 1.5, color: partyPal.ink, lineHeight: 1.35 }}>{payload.q}</Typography>
            {payload.a ? (
              <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 2.5, color: partyPal.gold }}>{payload.a}</Typography>
            ) : null}
          </RevealCard>
        )}

        {payload.layout === "topic" && (
          <RevealCard paper>
            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", opacity: 0.6 }}>
              {payload.h1}
            </Typography>
            <Typography className="party-display" sx={{ fontSize: 40, mt: 1.5, color: "inherit" }}>
              {payload.t}
            </Typography>
          </RevealCard>
        )}

        {payload.layout === "dare" && (
          <RevealCard paper={false}>
            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.15em", color: partyPal.inkDim }}>
              {payload.h1}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 2, color: partyPal.ink, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
              {payload.d ?? payload.w}
            </Typography>
          </RevealCard>
        )}
      </Stack>
    </Box>
  );
}
